/* eslint-disable camelcase -- race event types are in snake_case */
import { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { formatElapsedTime, formatRemainingTime } from 'src/utils/raceUtils'
import { formatTimeUntil } from 'src/utils/formatTimeUntil'
import { BET_CONFIG } from 'src/config'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import type { Race } from 'src/types/domain/Race'
import type { RaceScheduledEvent } from 'src/types/domain/RaceEvent'
import { LAST_CHANCE_S, hardCloseClock, hardCountdown, perfNow } from 'src/utils/hardCloseClock'

/** mm:ss, as `formatRemainingTime` writes it. */
const mmss = (totalSeconds: number) =>
  `${Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, '0')}:${(totalSeconds % 60).toString().padStart(2, '0')}`

interface EventInfo {
  label: string
  getTime: () => string
}

type Props = Readonly<{
  race: Race
  /**
   * The game's closing window (`bettingClosingWindow`), which the "Betting
   * Closing" readout counts down from. It used to be a literal 5: right for
   * the marble games, a second long on Birdie's 4 s close (B27 item 23).
   */
  closingSeconds?: number
}>

export const useTimer = ({
  race,
  closingSeconds = BET_CONFIG.DEFAULT_BETTING_CLOSING_WINDOW_SEC
}: Props) => {
  const [tick, setTick] = useState(0) // State to force re-render every second
  const { t } = useClientSideTranslation(useGetCurrentLanguage())

  const eventInfo = useMemo<EventInfo | undefined>(() => {
    if (!race.raceEvents.length) return undefined

    const eventMap = Object.fromEntries(race.raceEvents.map((event) => [event.eventType, event]))

    const { race_scheduled, race_starting, race_started, race_ended } = eventMap
    const firstEvent = race.raceEvents[0]?.eventType

    const getRaceScheduledInfo = () => {
      if (!race_scheduled) {
        return { raceScheduledEvent: null, remainingTime: '', isStartTime: true }
      }

      const raceScheduledEvent = race_scheduled as RaceScheduledEvent
      const startTimestamp = raceScheduledEvent.payload?.start_timestamp || ''
      const remainingTime: string = formatTimeUntil(startTimestamp, t as (key: string) => string)
      const isStartTime =
        dayjs(startTimestamp).isBefore(dayjs()) || !dayjs(startTimestamp).isValid()
      return { raceScheduledEvent, remainingTime, isStartTime }
    }

    const raceScheduledInfo = getRaceScheduledInfo()

    // A hard round (DESIGN §6 "Countdowns"): "Betting Closing" counts the last
    // seconds to the board's lock, then "Locked" counts X, on this page's own
    // arrival clock. Null on every other round: today's readouts below.
    const hardClock = hardCloseClock(race, undefined)
    const hardCount = hardClock ? hardCountdown(hardClock, perfNow()) : null
    const hardInfo: EventInfo | undefined =
      hardCount === null
        ? undefined
        : hardCount.stage === 'sending'
          ? { label: t('Locked'), getTime: () => mmss(hardCount.current) }
          : hardCount.current <= LAST_CHANCE_S
            ? { label: t('Betting Closing'), getTime: () => mmss(hardCount.current) }
            : { label: t('Race Status'), getTime: () => t('Betting Open') }

    const eventMappings: Record<string, EventInfo> = {
      race_scheduled: {
        label: raceScheduledInfo.isStartTime ? '' : t('Live in'),
        getTime: () => {
          return raceScheduledInfo.isStartTime
            ? (t('Starting soon') as string)
            : raceScheduledInfo.remainingTime
        }
      },
      race_ready: {
        label: t('Race Status'),
        getTime: () => t('Betting Open')
      },
      race_starting: {
        label: t('Betting Closing'),
        getTime: () => formatRemainingTime(race_starting?.eventTimestamp || '', closingSeconds)
      },
      race_started: {
        label: t('Race Running'),
        getTime: () => formatElapsedTime(race_started?.eventTimestamp || '')
      },
      race_ended: {
        label: t('Race Ended'),
        getTime: () =>
          formatElapsedTime(race_started?.eventTimestamp || '', race_ended?.eventTimestamp || '')
      },
      race_settled: {
        label: t('Race Settled'),
        getTime: () => ''
      }
    }

    if (hardInfo && (firstEvent === 'race_ready' || firstEvent === 'race_starting')) return hardInfo
    return firstEvent && eventMappings[firstEvent] ? eventMappings[firstEvent] : undefined
  }, [closingSeconds, race, t, tick])

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTick((tick) => tick + 1) // Trigger re-render to update eventInfo's time function
    }, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  return { eventInfo }
}
